import { Field, Input } from "@/components/ui/Input";

// The DOB filing type that goes into the AI1's download filename (SPE-15).
//
// DELIBERATELY THE QUIETEST THING ON THIS PAGE. The operator is here to check
// thirty sheets, not to answer a question about filenames, and the guess is
// right often enough that a required field or a dialog would be thirty
// interruptions to catch one error. So: one short row, pre-filled, skippable,
// and it never blocks Generate.
//
// It still has to be VISIBLE, which is the whole reason this exists rather than
// a config flag. The mapping behind the seeded value is read off 44 production
// filings and confirmed by nobody (docs/SHEET_PREFIX_MAPPING.md), so shipping it
// is only safe while the person who knows which filing this is can see what we
// decided and say otherwise. Hidden guesses are never corrected, and a wrong
// filing type goes to an examiner.
//
// THE EMPTY STATE MUST NOT READ AS A FAILURE, even though it is rarer than it
// was. Architectural sets — 24 of 44 filings — used to land blank because the
// prefixes could not separate GC from energy; the office has since said they are
// simply GC, so most filings now arrive named. What is left blank is a set built
// from prefixes nobody has told us about, which is a fair thing not to know and
// not something to apologise for on screen. Blank leaves the filename exactly as
// this app has always produced it.
export function FilingTypeField({
  value,
  userDefined,
  onChange,
}: {
  value: string;
  userDefined: boolean;
  /** Second argument is the new userDefined flag — true once they type, and
   *  false again if they clear a value they had typed, so the next extraction's
   *  reading is allowed back in. An empty box they never touched is not a
   *  decision; an empty box they emptied is, and only the flag tells them
   *  apart. */
  onChange: (value: string, userDefined: boolean) => void;
}) {
  // Short on purpose. Field lays the label and helper out on one row, so a long
  // helper beside a box this narrow wraps into two lines and the control starts
  // looking like a form to fill in rather than a value to glance at.
  const helper = userDefined ? "yours" : value ? "from the sheets" : "not in the sheets";

  return (
    <section className="mt-5 rounded-lg border border-rule bg-surface shadow-soft">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-3 px-6 py-5">
        <div className="w-44 shrink-0">
          <Field label="Filing type" helper={helper}>
            <Input
              id="paa-filing-type"
              value={value}
              // Uppercased on the way in: these are codes, the office writes
              // them in caps, and the filename should not record whether
              // somebody was holding shift.
              onChange={(e) => {
                const next = e.target.value.toUpperCase().slice(0, 8);
                onChange(next, next.length > 0);
              }}
              placeholder="—"
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
            />
          </Field>
        </div>
        <p className="min-w-0 flex-1 pb-2 text-[0.84rem] leading-relaxed text-ink-muted">
          {value
            ? <>Goes in the file name: <span className="font-mono text-ink-soft">…AI1 PAA {value} {"{job #}"}.pdf</span></>
            : "Left out of the file name unless you add one — these sheets don't say which type this is."}
        </p>
      </div>
    </section>
  );
}
